import React, { useState, useContext, useEffect } from "react";
import { CartContext } from "../context/CartContext";

function PaymentForm({ onSuccess, form, cart }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [form]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form || !form.name?.trim() || !form.email?.trim() || !form.address?.trim()) {
      setError("Please fill name, email and shipping address before paying.");
      return;
    }
    if (!cart || cart.length === 0) {
      setError("Your cart is empty. Add items before paying.");
      return;
    }
    const emailOk = /\S+@\S+\.\S+/.test(form.email);
    if (!emailOk) {
      setError("Please enter a valid email address.");
      return;
    }

    setError("");
    setLoading(true);
    // simulate a stub payment
    setTimeout(() => {
      setLoading(false);
      onSuccess();
    }, 800);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-[#f7f5ee] dark:bg-[#2d2a1b] rounded-lg border border-[#e7e3cf]">
        <label className="block text-sm font-medium mb-2">Payment method</label>
        <div className="p-3 bg-white dark:bg-[#1f1d14] rounded-md text-sm text-[#9a8d4c]">
          This is a stub payment processor (test mode). No card details are required.
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <button
        type="submit"
        className="w-full bg-primary text-background-dark py-3 rounded-xl font-black text-lg shadow-lg hover:brightness-105 transition-all"
        disabled={loading}
      >
        {loading ? "Processing…" : "Pay Now"}
      </button>
    </form>
  );
}

export default function Checkout() {
  const { cart, total, clearCart } = useContext(CartContext);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", address: "" });

  const onSuccess = () => {
    setSuccess(true);
    setForm({ name: "", email: "", address: "" });
    clearCart();
    localStorage.removeItem("user");
  };

  if (success) {
    return (
      <main className="max-w-[1280px] mx-auto px-6 py-8 min-h-[60vh] flex items-center justify-center">
        <div className="p-8 bg-white dark:bg-[#2d2a1b] rounded-2xl border border-[#e7e3cf] shadow-sm text-center w-full">
          <h2 className="text-2xl font-extrabold mb-2 flex items-center justify-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-green-50 text-green-600">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor" aria-hidden>
                <path d="M9.29 16.29a1 1 0 0 1-1.42 0l-3.59-3.59a1 1 0 0 1 1.42-1.42L9 13.17l7.88-7.88a1 1 0 0 1 1.42 1.42l-9.01 9.01z" />
              </svg>
            </span>
            Payment successful
          </h2>
          <p className="text-sm text-[#9a8d4c]">Thank you — your order is confirmed. We'll email you a receipt shortly.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-[1280px] mx-auto px-6 py-8">
      <h1 className="text-3xl font-extrabold mb-6">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className="lg:col-span-8">
          <div className="p-6 bg-white dark:bg-[#2d2a1b] rounded-2xl border border-[#e7e3cf] dark:border-[#3d3a2a] shadow-sm space-y-6">
            <h2 className="text-xl font-bold">Billing & Payment</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                className="w-full px-4 py-3 rounded-xl border border-[#e7e3cf] bg-transparent"
              />
              <input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                className="w-full px-4 py-3 rounded-xl border border-[#e7e3cf] bg-transparent"
              />
            </div>

            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Shipping address"
              className="w-full px-4 py-3 rounded-xl border border-[#e7e3cf] bg-transparent"
            />

            <PaymentForm onSuccess={onSuccess} form={form} cart={cart} />
          </div>
        </section>

        <aside className="lg:col-span-4">
          <div className="p-6 bg-white dark:bg-[#2d2a1b] rounded-2xl border border-[#e7e3cf] dark:border-[#3d3a2a] shadow-sm space-y-4">
            <h2 className="text-xl font-bold">Order Summary</h2>
            <div className="space-y-3">
              {cart.length === 0 ? (
                <p className="text-sm text-[#9a8d4c]">No items in cart.</p>
              ) : (
                cart.map((it) => (
                  <div key={it.id} className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold">{it.name}</div>
                      <div className="text-sm text-[#9a8d4c]">Qty {it.qty}</div>
                    </div>
                    <div className="text-sm font-bold">${((it.starting_price ?? it.price ?? 0) * (it.qty ?? 0)).toFixed(2)}</div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between text-sm text-[#9a8d4c]"><span>Subtotal</span><span>${total.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm text-[#9a8d4c]"><span>Shipping</span><span>${(total * 0.1).toFixed(2)}</span></div>
              <div className="mt-4 flex justify-between items-center">
                <div>
                  <div className="text-sm text-[#9a8d4c]">Total</div>
                  <div className="text-2xl font-extrabold">${(total * 1.1).toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}